/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import * as React from 'react';
import Paper from '@mui/material/Paper';
import { Theme } from '@mui/material/styles';
import { withStyles } from '@mui/styles';
import { Category, CategoryType } from '../common/Categories';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { LinearProgress } from '@mui/material';
import LegacyGrid from './legacy/LegacyGrid';
import Tooltip from '@mui/material/Tooltip';
import SearchSection from './SearchSection';
import Checkbox from '@mui/material/Checkbox';
import {
    loadFromLocalStorage,
} from '../common/localStorage';

import './DataTable.css';
import './styles.css';
import SimpleDialogDemo from './AssignToDialog';
import 'jspdf-autotable';
import TableViewCol from './TableViewCol';
import Edit from "./Edit";
import Open from './Open';
import Export from './Export';
import { sendRequestAndReceiveResponse } from '../common/common';
import { fetchAuthorizationsEndpoint } from '../common/constants';
import { GridGraphqlExtras, toAuthorizationGraphqlBody } from '../common/graphqlJsonBody';

const styles = (theme: Theme) => ({
});

interface Props {
    selectedCategory: Category | undefined;
    totalRows: number;
    classes?: any;
    refreshChart?: Function;
    data?: any
    loadData: Function;
    exportData: Function
    isloading: boolean;
    error?: any
}
const TableSort: React.FunctionComponent<{
    active: boolean;
    direction: 'asc' | 'desc';
    sortByColumn: string;
    onClick: any;
    children?: React.ReactNode;
}> = props => {
    const clickHanlder = () => {
        props.onClick(props.sortByColumn);
    };
    return (
        <Tooltip title="Sort">
            <TableSortLabel
                active={props.active}
                direction={props.direction}
                onClick={clickHanlder}
            >
                {props.children}
            </TableSortLabel>
        </Tooltip>
    );
};

class DataGrid extends React.PureComponent<Props, any> {
    state = {
        pageSizes: [10, 20, 30, 40, 50],
        rows: undefined,
        selection: undefined,
        sortOrder: 'asc',
        sortOrderBy: '',
        filters: [],
        loading: false,
        pageSize: 10,
        currentPage: 0,
        totalCount: 0,
        selected: [] as any,
        selectedObject: [] as any,
        isViewColumnOpen: false,
        columns: [],
        isSelectOnlyOne: false,
        isCheckBoxEnabled: false,
    };
    lastQuery: string | undefined;

    componentDidMount() {
        if (this.props.selectedCategory !== undefined) {
            this.setState({
                columns: this.props.selectedCategory.columns, isSelectOnlyOne: this.props.selectedCategory.isSelectOneOnly,
                isCheckBoxEnabled: this.props.selectedCategory.isCheckBoxEnabled
            });
        }

        if (this.props.selectedCategory !== undefined && (this.props.selectedCategory.id === 1 || this.props.selectedCategory.id === 3)) {
            const clientMode: any = loadFromLocalStorage('Client Mode');
            if (clientMode != null && clientMode !== undefined && clientMode === 'Desktop') {
                this.setState({ isCheckBoxEnabled: true });
            }
            else {
                this.setState({ isCheckBoxEnabled: false });
            }
        }
    }

    getTableRow = (rowItem: any, isSelected: boolean, selectedCategoryType: CategoryType) => {
        return (
            <TableRow
                className={`dtable-row ${isSelected ? 'selected' : ''}`}
                key={rowItem.id}
                hover
                onClick={event => this.handleClick(event, rowItem.id, rowItem)}
                role="checkbox"
                aria-checked={isSelected}
                tabIndex={-1}
                selected={isSelected}
            >
                {(this.props.selectedCategory !== undefined &&
                    this.state.isCheckBoxEnabled === true) &&
                    (
                        <TableCell className="dtable-cell" padding="none">
                            <Checkbox checked={isSelected} />
                        </TableCell>
                    )}
                {this.props.selectedCategory !== undefined && this.state.columns.map((column: any, index: any) => {
                    return (

                        (column.display === true) && <TableCell
                            className="dtable-cell"
                            key={column.name}
                            id={column.name}
                            style={{ maxWidth: column.width }}
                        >
                            <div
                                style={{
                                    height: 20,
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {rowItem[column.name]}
                            </div>
                        </TableCell>

                    );
                })}
            </TableRow>
        );
    };

    gridGraphqlExtras(): GridGraphqlExtras {
        const { sortOrder, sortOrderBy, pageSize, currentPage, filters } = this.state;
        const extras: GridGraphqlExtras = {
            pageno: Number(currentPage) + 1,
            pagesize: Number(pageSize)
        };
        if (sortOrderBy) {
            extras.orderby = `${sortOrderBy}:${sortOrder}`;
        }
        if (filters && filters.length >= 1) {
            extras.filters = filters;
        }
        return extras;
    }

    queryKey() {
        return JSON.stringify(this.gridGraphqlExtras());
    }

    loadData() {
        const extras = this.gridGraphqlExtras();
        const queryKey = this.queryKey();
        if (queryKey === this.lastQuery) {
            return;
        }

        this.props.loadData(extras);
        this.lastQuery = queryKey;
    }

    changeSorting = (sortColumnName: string) => {
        if (navigator.onLine) {
            const sortOrderBy = sortColumnName;
            let sortOrder: 'desc' | 'asc' = 'desc';

            if (
                this.state.sortOrderBy === sortOrderBy &&
                this.state.sortOrder === 'desc'
            ) {
                sortOrder = 'asc';
            }
            this.setState((prevState: any, props: any) => {
                if (prevState.sortOrderBy !== sortOrderBy || prevState.sortOrder !== sortOrder) {
                    return {
                        sortOrderBy,
                        sortOrder,
                        currentPage: 0
                    };
                }
                else {
                    return {
                        sortOrderBy,
                        sortOrder
                    };
                }
            }, () => {
                this.loadData();
            });
        }
    };
    changeCurrentPage = (event: any, currentPage: number) => {
        if (navigator.onLine) {
            this.setState({ currentPage }, () => {
                this.loadData();
            });
        }
    };
    changeRowsPerPage = (event: any) => {
        if (navigator.onLine) {
            this.setState({ pageSize: event.target.value }, () => {
                this.loadData();
            });
        }
    };
    onSearch = (filters: any) => {
        const formattedFilters = filters.suggestionsFilter.map((filter: any) => {
            let jsondate = '';
            if (
                filter.filterProp === 'opendate' ||
                filter.filterProp === 'closeddate'
            ) {
                jsondate = new Date(filter.filterText).toJSON();
                return { ...filter, filterText: `"${jsondate}"` };
            }
            if (filter.filterProp === 'id' || filter.filterProp === 'age') {
                return { ...filter, filterText: parseInt(filter.filterText) };
            }
            let x: any = filter.filterText;
            if (x.indexOf('\\') !== -1) {
                x = x.replace(/\\/g, '\\\\');
            }
            if (x.indexOf('"') !== -1) {
                x = x.replace(/"/g, '\\"');
            }

            return { ...filter, filterText: `"${x}"` };
        });
        this.setState((prevState: any, props: any) => {
            if (prevState.filters !== formattedFilters) {
                return {
                    filters: formattedFilters,
                    currentPage: 0
                };
            }
            else {
                return {
                    filters: formattedFilters
                };
            }
        }, () => {
            this.loadData();
            this.props.refreshChart !== undefined && this.props.refreshChart(this.state.filters);
        });
    };

    changeSelection(selectionData: any) {
        this.setState({ selection: selectionData });
    }
    exportList: Array<object> = [{ id: 0, name: 'CSV' }, { id: 1, name: 'PDF' }];
    handleExport = async (exportType: any, exportColumns: any) => {
        this.props.exportData(exportType, this.state.columns);
    };

    getTotalCountOnExport = async () => {
        if (this.props.selectedCategory !== undefined) {
            const selectedCategory: Category = this.props.selectedCategory;
            if (selectedCategory.requestBodyForGridTotalCount !== undefined) {
                let exportTotalCount = 0;
                const body = toAuthorizationGraphqlBody(
                    selectedCategory.requestBodyForGridTotalCount,
                    selectedCategory.id,
                    { pageno: 1, pagesize: 10 });
                const responseData = await sendRequestAndReceiveResponse(body, fetchAuthorizationsEndpoint);
                if (responseData !== undefined && responseData.data !== undefined) {
                    switch (selectedCategory.id) {
                        case 8:
                            exportTotalCount = responseData.data.trainings.count;
                            break;
                        case 9:
                            exportTotalCount = responseData.data.trainings.count;
                            break;
                        case 10:
                            exportTotalCount = responseData.data.productauthorizations.count;
                            break;
                        case 11:
                            exportTotalCount = responseData.data.trainings.count;
                            break;
                        case 12:
                            exportTotalCount = responseData.data.productauthorizations.count;
                            break;
                    }
                }
                return exportTotalCount;
            }
        }
    };

    handleSelectAllClick = (event: any) => {
        if (event.target.checked) {
            this.setState((state: any) => ({
                selected: this.props.data.map((rowItem: any) => rowItem.id)
            }));
            return;
        }
        this.setState({ selected: [] });
    };

    handleClick = (event: any, id: number, rowItem: any) => {
        let { selected } = this.state;
        const selectedIndex = selected.indexOf(id);
        if (this.state.isSelectOnlyOne && selectedIndex !== 0) {
            selected = [];
        }
        let newSelected: any = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }
        this.setState({ selected: newSelected }, () => {
            if (this.state.selected.length === 0) {
                this.setState({ selectedObject: [] });
            }
            else {
                this.setState({ selectedObject: rowItem });
            }
        });

    };
    isSelected = (id: number) => this.state.selected.indexOf(id) !== -1;

    toggleViewColumn = (index: number) => {

        this.setState(
            (prevState: any, props: any) => {
                const columns = Object.assign([], prevState.columns);
                columns[index].display = columns[index].display !== true;
                return {
                    columns
                };
            });
    };
    public render(): React.ReactNode {
        const {
            pageSizes,
            sortOrder,
            sortOrderBy,
            pageSize,
            currentPage,
            selected,
            columns
        } = this.state;
        const { error } = this.props;
        const gridData = this.props.data;
        const totalCount = this.props.totalRows;
        const selectedColumns: any = this.state.columns.filter((x: any) => {
            return x.display === true;
        });
        const loading = this.props.isloading;
        const filteredSearchSuggestions = this.props.selectedCategory !== undefined && this.props.selectedCategory.filters().filter(
            (suggestion: any) => {
                return (
                    this.state.filters.filter((x: any) => {
                        return x.filterProp === suggestion.searchProp;
                    }).length === 0
                );
            }
        );
        const numSelected = selected.length;
        return (
            <LegacyGrid>
                <LegacyGrid container={true}>
                    <LegacyGrid
                        item
                        style={{
                            display: 'flex',
                            alignItems: 'right',
                            flex: '1',
                            justifyContent: 'flex-end'
                        }}
                    >
                        {(this.props.selectedCategory !== undefined && this.props.selectedCategory.isAssignToEnabled === true) && (
                            <SimpleDialogDemo
                                assigntodisabled={
                                    this.state.selected.length === 0
                                }
                                serviceCaseList={this.state.selected}
                            />
                        )}
                        {this.props.selectedCategory !== undefined && this.props.selectedCategory.isOpenEnabled === true && <Open selected={this.state.selected} />}
                        {this.props.selectedCategory !== undefined && this.props.selectedCategory.isEditEnabled === true &&
                            <Edit selected={this.state.selectedObject} selectedCategory={this.props.selectedCategory} />}
                        {(this.props.selectedCategory !== undefined && this.props.selectedCategory.isExportEnabled === true) &&
                            <Export selectedColumns={selectedColumns} isDisabled={false} exportList={this.exportList} handleExport={this.handleExport} totalCount={totalCount}
                                getTotalCountOnExport={this.getTotalCountOnExport} isSearchApplied={this.state.filters.length > 0} selectTitle={`Export`} />
                        }
                    </LegacyGrid>
                </LegacyGrid>
                <br />
                <LegacyGrid>
                    <Paper>
                        <LegacyGrid container={true}>
                            <LegacyGrid item md={11}>
                                <SearchSection
                                    onSearch={this.onSearch}
                                    searchSuggestions={filteredSearchSuggestions}
                                />
                            </LegacyGrid>
                            <LegacyGrid item md={1}>
                                {this.props.selectedCategory !== undefined &&
                                    <TableViewCol columns={columns} onColumnUpdate={this.toggleViewColumn} />
                                }
                            </LegacyGrid>
                        </LegacyGrid>
                        <div style={{ width: '100%', height: 'auto', overflowX: 'auto' }}>
                            <Table id="gridTable" className="dtable">
                                <TableHead>
                                    <TableRow className="dtable-row-head">
                                        {(this.props.selectedCategory !== undefined && this.state.isCheckBoxEnabled === true && gridData != null && gridData.length !== 0) && (
                                            <TableCell className="dtable-cell" padding="checkbox">
                                                <Checkbox
                                                    indeterminate={
                                                        numSelected > 0 &&
                                                        numSelected < (gridData && gridData.length)
                                                    }
                                                    checked={
                                                        numSelected === (gridData && gridData.length)
                                                    }
                                                    onChange={this.handleSelectAllClick}
                                                    hidden={this.state.isSelectOnlyOne}
                                                />
                                            </TableCell>
                                        )}
                                        {this.props.selectedCategory !== undefined && columns.map((column: any) => {
                                            return (
                                                (column.display === true) && (<TableCell
                                                    className="dtable-cell"
                                                    key={column.name}
                                                    sortDirection={
                                                        (sortOrderBy === column.name
                                                            ? sortOrder
                                                            : false) as 'asc' | 'desc' | false
                                                    }
                                                    style={{
                                                        maxWidth: column.width,
                                                        fontWeight: 'bolder'
                                                    }}
                                                >
                                                    <TableSort
                                                        active={sortOrderBy === column.name}
                                                        direction={sortOrder as 'asc' | 'desc'}
                                                        onClick={this.changeSorting}
                                                        sortByColumn={column.name}
                                                    >
                                                        {column.title}
                                                    </TableSort>
                                                </TableCell>)

                                            );
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        (gridData != null &&
                                            gridData.length !== 0) && error === undefined ?
                                            gridData.map((rowItem: any) => {
                                                const isSelected = this.isSelected(rowItem.id);
                                                return this.getTableRow(rowItem, isSelected, this.props.selectedCategory !== undefined ?
                                                    this.props.selectedCategory.type : CategoryType.ServiceCase);
                                            }) :
                                            !loading && error !== undefined ?
                                                <TableRow>
                                                    <TableCell id="norecords" colSpan={12} align='center' className='no-records'>
                                                        An error occurred while processing this request.</TableCell>
                                                </TableRow> :
                                                !loading &&
                                                <TableRow>
                                                    <TableCell id="norecords" colSpan={12} align='center' className='no-records'>No records to display</TableCell>
                                                </TableRow>
                                    }
                                </TableBody>
                            </Table>
                        </div>
                        {loading && <LinearProgress color="secondary" />}
                        <TablePagination
                            rowsPerPageOptions={pageSizes}
                            component="div"
                            count={totalCount == undefined ? 0 : totalCount}
                            rowsPerPage={pageSize}
                            page={currentPage}
                            backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                            nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                            onPageChange={(_e, newPage) => this.changeCurrentPage(_e, newPage)}
                            onRowsPerPageChange={this.changeRowsPerPage}
                        />
                    </Paper>
                </LegacyGrid>
            </LegacyGrid>
        );
    }
}

export default (withStyles(styles, { withTheme: true })(DataGrid));
